import { gql } from "@codesandbox/api";
import { useEnvironmentInterface } from "environment-interface";
import type { VisibilityNumber } from "features/editor/sandboxUtils";
import type { Sandbox } from "features/types/sandbox";
// @ts-expect-error no types
import humps from "humps";

import { csbApi } from "utils/csbApi";

const { decamelizeKeys } = humps;

export type CustomTemplate = {
  color?: string;
  iconUrl?: string;
  id: string;
  published?: boolean;
  title: string;
  url: string | null;
  v2: boolean | null;
};

const renameSandboxQuery = gql.createMutation(
  "RenameSandbox",
  ({ id, title }: { id: string; title: string }) => ({
    renameSandbox: [
      { id, title },
      {
        title: true,
        alias: true,
      },
    ],
  }),
);

export const renameSandbox = (id: string, title: string) => {
  return csbApi.gql.query(renameSandboxQuery, { id, title });
};

const changeSandboxPrivacyQuery = gql.createMutation(
  "ChangeSandboxPrivacy",
  ({ sandboxIds, privacy }: { sandboxIds: string[]; privacy: number }) => ({
    setSandboxesPrivacy: [
      { sandboxIds, privacy },
      {
        id: true,
      },
    ],
  }),
);

export const changeSandboxPrivacy = (
  sandboxId: string,
  privacy: "public" | "unlisted" | "private",
) => {
  const privacyNumber = { public: 0, unlisted: 1, private: 2 }[privacy];

  return csbApi.gql.query(changeSandboxPrivacyQuery, {
    sandboxIds: [sandboxId],
    privacy: privacyNumber,
  });
};

export const useChangeSandboxInfo = (sandboxId: string) => {
  const { csbApi, api } = useEnvironmentInterface();

  if (!csbApi) {
    return () => {
      throw new Error("CodeSandbox API is not initialised");
    };
  }

  return async (
    params: Partial<{
      title: string;
      description: string;
      privacy: VisibilityNumber;
      is_frozen: boolean;
    }>,
  ) =>
    csbApi.rest
      .put<{ data: Sandbox }>({
        path: `/v1/sandboxes/${sandboxId}`,
        headers: {
          Authorization: `Bearer ${await api.getJwtToken()}`,
        },
        data: {
          sandbox: params,
        },
      })
      .then((res) => res.data);
};

export const useChangeTemplateInfo = (
  sandboxId: string,
  templateId: string | undefined,
) => {
  const { csbApi, api } = useEnvironmentInterface();

  if (!csbApi) {
    return async () => {
      throw new Error("CodeSandbox API is not initialised");
    };
  }

  if (!templateId) {
    return async () => {
      // eslint-disable-next-line no-console
      console.warn("Attempted to change template info without a template id");
    };
  }

  return async (params: Partial<CustomTemplate>) =>
    csbApi.rest
      .put<{ data: CustomTemplate }>({
        path: `/v1/sandboxes/${sandboxId}/templates/${templateId}`,
        headers: {
          Authorization: `Bearer ${await api.getJwtToken()}`,
        },
        data: {
          template: decamelizeKeys(params),
        },
      })
      .then((res) => res.data);
};

export const useUploadFile = () => {
  const { csbApi, api } = useEnvironmentInterface();

  if (!csbApi) {
    return () => {
      throw new Error("CodeSandbox API is not initialised");
    };
  }

  return async (fileName: string, fileContent: string) =>
    csbApi.rest
      .post<{ data: { url: string } }>({
        path: `/v1/users/current_user/uploads`,
        headers: {
          Authorization: `Bearer ${await api.getJwtToken()}`,
        },
        data: {
          name: fileName,
          content: fileContent,
        },
      })
      .then((res) => res.data);
};

export const useCreateTemplate = (sandboxId: string) => {
  const { csbApi, api } = useEnvironmentInterface();

  if (!csbApi) {
    return () => {
      throw new Error("CodeSandbox API is not initialised");
    };
  }

  return async (title: string, description: string, color: string) =>
    csbApi.rest
      .post<{ template: CustomTemplate }>({
        path: `/v1/sandboxes/${sandboxId}/templates`,
        headers: {
          Authorization: `Bearer ${await api.getJwtToken()}`,
        },
        data: {
          template: {
            color,
            description,
            title,
          },
        },
      })
      .then((res) => res.template);
};

export const useDeleteTemplate = (sandboxId: string) => {
  const { csbApi, api } = useEnvironmentInterface();

  if (!csbApi) {
    return () => {
      throw new Error("CodeSandbox API is not initialised");
    };
  }

  return async (templateId: string) =>
    csbApi.rest.delete({
      path: `/v1/sandboxes/${sandboxId}/templates/${templateId}`,
      headers: {
        Authorization: `Bearer ${await api.getJwtToken()}`,
      },
    });
};
